import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaExternalLinkAlt } from 'react-icons/fa';
import { api } from '../../App';
import { AdminLayout } from './AdminDashboard';
import './Admin.css';

const AdminPosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, [statusFilter]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const query = statusFilter 
        ? `/api/admin/posts?status=${statusFilter}` 
        : '/api/admin/posts';
      const data = await api.get(query);
      setPosts(data.posts || []);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId, postTitle) => {
    if (!window.confirm(`Ali ste prepričani, da želite izbrisati "${postTitle}"?`)) {
      return;
    }

    try {
      await api.delete(`/api/admin/posts/${postId}`);
      setPosts(posts.filter(p => p.id !== postId));
    } catch (error) {
      console.error('Failed to delete post:', error);
      alert('Napaka pri brisanju objave');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('sl-SI', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout title="Objave">
      {/* Header Actions */}
      <div className="posts-header">
        <div className="posts-actions">
          <div style={{ position: 'relative' }}>
            <FaSearch style={{ 
              position: 'absolute', 
              left: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)'
            }} />
            <input
              type="text"
              placeholder="Išči objave..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              style={{ paddingLeft: '40px' }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-select"
            style={{ width: 'auto', padding: '10px 16px' }}
          >
            <option value="">Vsi statusi</option>
            <option value="published">Objavljeno</option>
            <option value="draft">Osnutek</option>
            <option value="archived">Arhivirano</option>
          </select>
        </div>
        <Link to="/admin/posts/new" className="btn btn-primary">
          <FaPlus /> Nova objava
        </Link>
      </div>

      {/* Posts Table */}
      <motion.div
        className="posts-table"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {loading ? (
          <div className="loading-container" style={{ padding: '60px' }}>
            <div className="loading-spinner"></div>
            <p>Nalaganje objav...</p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="empty-state">
            <h3>Ni najdenih objav</h3>
            <p>{searchQuery ? 'Poskusite z drugim iskalnim nizom.' : 'Ustvarite prvo objavo za začetek.'}</p>
            {!searchQuery && (
              <Link to="/admin/posts/new" className="btn btn-primary">Ustvari objavo</Link>
            )}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Naslov</th>
                  <th>Kategorija</th>
                  <th>Status</th>
                  <th>Ustvarjeno</th>
                  <th>Ogledi</th>
                  <th>Akcije</th>
                </tr>
              </thead>
              <tbody>
                {filteredPosts.map((post) => (
                  <tr key={post.id}>
                    <td className="post-title-cell">
                      <Link to={`/admin/posts/${post.id}`} className="post-title-link">
                        {post.title}
                      </Link>
                    </td>
                    <td>
                      <span className="category-tag">{post.category}</span>
                    </td>
                    <td>
                      <span className={`status-badge ${post.status}`}>
                        {post.status}
                      </span>
                    </td>
                    <td>{formatDate(post.created_at)}</td>
                    <td>{post.views || 0}</td>
                    <td>
                      <div className="action-buttons">
                        {post.status === 'published' && (
                          <a 
                            href={`/blog/${post.slug}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="action-btn"
                            title="Ogled objave"
                          >
                            <FaExternalLinkAlt />
                          </a>
                        )}
                        <Link 
                          to={`/admin/posts/${post.id}`} 
                          className="action-btn"
                          title="Uredi objavo"
                        >
                          <FaEdit />
                        </Link>
                        <button 
                          onClick={() => handleDelete(post.id, post.title)}
                          className="action-btn delete"
                          title="Izbriši objavo"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </AdminLayout>
  );
};

export default AdminPosts;

